import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import SendNotification from "./components/SendNotification";
import InactiveMinersNotification from "./components/InactiveMinersNotification";
import toast, { Toaster } from "react-hot-toast";
import { FiEdit2, FiTrash2, FiEye, FiX, FiPlus, FiExternalLink } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const imageBaseUrl = process.env.REACT_APP_API_BASE_IMAGE_URL;
const API_URL = `${baseUrl}/announcement`;


const emptyForm = { title: "", description: "", image: "", url: "", username: "admin", isAds: false };

const Announcement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [viewedAnnouncement, setViewedAnnouncement] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const limit = 9;
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("token");

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/list?page=${page}&limit=${limit}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAnnouncements(data.data || []);
      setTotal(data.pagination?.total || 0);
      setLastPage(data.pagination?.lastPage || 1);
    } catch {
      toast.error("Failed to fetch announcements");
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const method = editingId ? "PUT" : "POST";
    const url = editingId ? `${API_URL}/edit/${editingId}` : `${API_URL}/create`;

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("description", form.description);
    formData.append("url", form.url);
    formData.append("username", form.username);
    formData.append("isAds", form.isAds);
    if (imageFile) formData.append("image", imageFile);

    try {
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(editingId ? "Updated successfully!" : "Created successfully!");
        handleCancel();
        setPage(1);
        fetchAnnouncements();
      } else {
        toast.error(result.message || "Something went wrong");
      }
    } catch {
      toast.error("Error saving announcement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setForm({ title: item.title, description: item.description, url: item.url || "", username: item.username || "admin", image: item.image, isAds: Number(item.isAds) === 1 });
    setEditingId(item.id);
    setImageFile(null);
    setImagePreview(item.image ? `${imageBaseUrl}/${item.image}` : null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      const res = await fetch(`${API_URL}/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Deleted successfully!");
        setPage(1);
        fetchAnnouncements();
      } else {
        toast.error("Failed to delete");
      }
    } catch {
      toast.error("Network error");
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-8">

          {/* Page heading */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Announcements</h2>
            <p className="text-sm text-gray-400 mt-0.5">Create and manage announcements</p>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-700 mb-5">
              {editingId ? "Edit Announcement" : "Create Announcement"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Title <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  placeholder="Enter title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description <span className="text-red-400">*</span></label>
                <textarea
                  placeholder="Enter description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] resize-none"
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Banner Image</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full text-sm text-gray-500 border border-gray-200 rounded-lg px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-[#ffffe0] file:text-[#999900] hover:file:bg-[#ffffcc]"
                  />
                  {imagePreview && (
                    <div className="mt-2 relative w-fit">
                      <img src={imagePreview} alt="preview" className="h-20 rounded-lg object-cover border" />
                      <button
                        type="button"
                        onClick={() => { setImagePreview(null); setImageFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <FiX size={10} />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Button URL</label>
                  <input
                    type="text"
                    placeholder="https://..."
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-gray-500">Is Ad</label>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, isAds: !form.isAds })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${form.isAds ? "bg-[#ffff00]" : "bg-gray-200"}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isAds ? "translate-x-6" : "translate-x-1"}`}
                  />
                </button>
                <span className="text-xs text-gray-500">{form.isAds ? "Yes" : "No"}</span>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium px-5 py-2.5 rounded-lg transition"
                >
                  <FiPlus size={15} />
                  {submitting ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Announcement list */}
          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-4">
              All Announcements
              <span className="ml-2 text-xs font-normal text-gray-400">({total})</span>
            </h3>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl h-56 animate-pulse" />
                ))}
              </div>
            ) : announcements.length === 0 ? (
              <div className="bg-white rounded-xl p-10 text-center text-gray-400 text-sm">
                No announcements yet. Create one above.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {announcements.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                    {item.image ? (
                      <img
                        src={`${imageBaseUrl}${item.image}`}
                        alt={item.title}
                        className="w-full h-40 object-cover"
                      />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-[#ffffe0] to-indigo-100 flex items-center justify-center text-[#cccc00] text-4xl">
                        📢
                      </div>
                    )}
                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-800 text-sm leading-snug line-clamp-1 flex-1">{item.title}</h4>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${Number(item.isAds) === 1 ? "bg-[#ffffcc] text-[#999900]" : "bg-gray-100 text-gray-400"}`}>
                          {Number(item.isAds) === 1 ? "AD" : "No Ad"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 flex-1">{item.description}</p>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-xs text-[#b8b800] hover:underline"
                        >
                          <FiExternalLink size={11} /> Visit Link
                        </a>
                      )}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => setViewedAnnouncement(item)}
                          className="flex items-center gap-1 text-xs text-[#b8b800] hover:bg-[#ffffe0] px-2.5 py-1.5 rounded-lg transition"
                        >
                          <FiEye size={13} /> View
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="flex items-center gap-1 text-xs text-amber-500 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition"
                        >
                          <FiEdit2 size={13} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition ml-auto"
                        >
                          <FiTrash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {lastPage > 1 && (
              <div className="flex items-center justify-center gap-1 mt-6">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Prev
                </button>
                {(() => {
                  const start = Math.max(1, Math.min(page - 2, lastPage - 4));
                  const end = Math.min(lastPage, start + 4);
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => setPage(pg)}
                      className={`px-3 py-1.5 text-sm border rounded-lg transition ${page === pg ? "bg-[#ffff00] text-black border-[#ffff00]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                    >
                      {pg}
                    </button>
                  ));
                })()}
                <button
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  disabled={page === lastPage}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Notification panels */}
          <div className="grid sm:grid-cols-2 gap-5">
            <SendNotification />
            <InactiveMinersNotification />
          </div>

        </div>
      </div>

      {/* View Modal */}
      {viewedAnnouncement && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setViewedAnnouncement(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {viewedAnnouncement.image && (
              <img
                src={`${imageBaseUrl}${viewedAnnouncement.image}`}
                alt="announcement"
                className="w-full h-52 object-cover"
              />
            )}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-lg font-bold text-gray-800 flex-1">{viewedAnnouncement.title}</h2>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${Number(viewedAnnouncement.isAds) === 1 ? "bg-[#ffffcc] text-[#999900]" : "bg-gray-100 text-gray-400"}`}>
                  {Number(viewedAnnouncement.isAds) === 1 ? "AD" : "No Ad"}
                </span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{viewedAnnouncement.description}</p>
              {viewedAnnouncement.url && (
                <a
                  href={viewedAnnouncement.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm text-[#999900] hover:underline"
                >
                  <FiExternalLink size={14} /> Open Link
                </a>
              )}
              <button
                onClick={() => setViewedAnnouncement(null)}
                className="mt-5 w-full py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcement;
